import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGoogleCSE } from '../hooks/useGoogleCSE';
import { searchService } from '../services/searchService';
import './Search.css';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [searchState, setSearchState] = useState({
    aiAnswer: '',
    loading: false,
    error: '',
    searchResults: [],
    intent: '',
    groundingMetadata: null,
    aiLoading: false
  });

  const { containerRef, createSearchContainer, extractGoogleResults, triggerSearch } = useGoogleCSE();
  const PAGE_CONTAINER_ID = 'page-cse-container';

  // Handle URL parameters
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (urlQuery) {
      setQuery(urlQuery);
      handleSearch(urlQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Create search container when component mounts
  useEffect(() => {
    createSearchContainer(PAGE_CONTAINER_ID);
  }, [createSearchContainer]);

  const handleSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;

    setSearchState(prev => ({
      ...prev,
      loading: true,
      error: '',
      aiAnswer: '',
      searchResults: [],
      intent: '',
      groundingMetadata: null
    }));

    try {
      triggerSearch(searchQuery, PAGE_CONTAINER_ID);

      // 1. FAST LOCAL SEARCH
      const localData = await searchService.performLocalSearch(searchQuery);

      if (localData.error) {
        setSearchState(prev => ({ ...prev, error: localData.error, loading: false }));
        return;
      }

      setSearchState(prev => ({
        ...prev,
        intent: localData.intent,
        searchResults: localData.searchResults || [],
        loading: false
      }));

      // Combined results with Google CSE
      setTimeout(() => {
        const googleResults = extractGoogleResults(PAGE_CONTAINER_ID);
        const combinedResults = googleResults.length > 0 ? googleResults : localData.searchResults || [];
        setSearchState(prev => ({ ...prev, searchResults: combinedResults }));
      }, 1500);

      // 2. AI RESPONSE
      if (localData.intent === 'AI_RESPONSE') {
        if (localData.cachedAnswer) {
          setSearchState(prev => ({
            ...prev,
            aiAnswer: searchService.cleanAIResponse(localData.cachedAnswer.answer),
            groundingMetadata: localData.cachedAnswer.groundingMetadata
          }));
        } else {
          setSearchState(prev => ({ ...prev, aiLoading: true }));
          searchService.performAISearch(searchQuery, localData.searchResults || [])
            .then(aiData => {
              if (aiData.answer) {
                setSearchState(prev => ({
                  ...prev,
                  aiAnswer: searchService.cleanAIResponse(aiData.answer),
                  groundingMetadata: aiData.groundingMetadata,
                  aiLoading: false
                }));
              } else {
                setSearchState(prev => ({ ...prev, aiLoading: false }));
              }
            })
            .catch(() => setSearchState(prev => ({ ...prev, aiLoading: false })));
        }
      }

    } catch (err) {
      console.error('Search error:', err);
      setSearchState(prev => ({
        ...prev,
        error: 'Search failed. Please try again.',
        loading: false
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    handleSearch(query);
  };

  const { aiAnswer, loading, error, searchResults, intent, groundingMetadata } = searchState;

  return (
    <div className="search-page">
      <form onSubmit={handleSubmit} className="search-form">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search NBA Finals articles or ask a question..."
          className="search-input"
        />
        <button type="submit" className="search-button" disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {/* Rest of your existing JSX remains the same */}
      {searchState.aiLoading && (
        <div className="google-lima-container">
          <div className="lima-header">
            <div className="lima-icon">✨</div>
            <h3 className="lima-title">Thinking...</h3>
          </div>
          <div className="lima-content skeleton-pulse" style={{ height: '60px', borderRadius: '4px', background: 'rgba(255,255,255,0.05)' }}></div>
        </div>
      )}

      {intent === 'AI_RESPONSE' && aiAnswer && (
        <div className="google-lima-container">
          <div className="lima-header">
            <div className="lima-icon">🤖</div>
            <h3 className="lima-title">Google Lima</h3>
          </div>
          <div className="lima-content">{aiAnswer}</div>
          {groundingMetadata?.webSearchQueries && (
            <div className="lima-sources">
              <small>Sources: Web search and NBA Finals articles</small>
            </div>
          )}
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="search-results-container">
          <h3 className="results-title">
            {intent === 'AI_RESPONSE' ? 'Related Articles' : 'Search Results'}
          </h3>
          <div className="search-results-grid">
            {searchResults.slice(0, 6).map((result, idx) => (
              <div
                key={idx}
                className="search-result-card"
                onClick={() => window.open(result.link, '_blank')}
              >
                <h4 className="result-title">{result.title}</h4>
                <p className="result-snippet">{result.snippet}</p>
                <div className="result-url">{result.link}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Analyzing your search and finding the best results...</span>
        </div>
      )}

      {/* Page-specific Google CSE container */}
      <div ref={containerRef} className="google-cse-hidden"></div>
    </div>
  );
}